<script setup>
import { createError, navigateTo, useHead, useSeoMeta } from "#imports";
import { useQueryCurrentPage } from "@voix/composables/queries/useQueryCurrentPage";
import { buildHeadData, buildSeoMetaData } from "@voix/composables/useHeadTagsBuilder";
import { useVoixConfig } from "@voix/composables/useVoixConfig";
import { usePageStore } from "@voix/store/pageStore";
import { getVoixUserXP } from "@voix/utils/voixUserXP";
import { onMounted, reactive, watch } from "vue";
import { useRoute } from "vue-router";
const route = useRoute();
const pageStore = usePageStore();
let path = route.path;
if (path !== "/" && path.startsWith("/"))
  path = path.substring(1, path.length);
const { getCurrentDomain } = useVoixConfig();
const currentDomain = getCurrentDomain();
if (!path || !currentDomain || path.includes(".")) {
  throw createError({
    fatal: true,
    statusCode: 404
  });
}
const { getCurrentPage, fetchCurrentPage } = useQueryCurrentPage();
async function loadPage() {
  await getCurrentPage(
    reactive({
      path
    })
  );
}
await loadPage();
function applyHeadTags() {
  if (pageStore.currentPage) {
    useSeoMeta(buildSeoMetaData());
    useHead(buildHeadData());
  }
}
applyHeadTags();
if (pageStore.inStudioIframe) {
  watch(
    () => pageStore.currentPage?.head_tags,
    () => {
      applyHeadTags();
    },
    { deep: true }
  );
  watch(
    () => pageStore.currentPage?.global_head_tags,
    () => {
      applyHeadTags();
    },
    { deep: true }
  );
  watch(
    () => pageStore.currentPage?.id,
    () => {
      applyHeadTags();
    }
  );
}
if (pageStore.pageResponse.redirect?.to && (pageStore.pageResponse.status === 301 || pageStore.pageResponse.status === 302)) {
  let redirectTo = pageStore.pageResponse.redirect.to;
  const queryParams = new URLSearchParams(route.query).toString();
  if (queryParams) {
    if (redirectTo.includes("?"))
      redirectTo += `&${queryParams}`;
    else
      redirectTo += `?${queryParams}`;
  }
  await navigateTo(
    redirectTo,
    {
      external: redirectTo.startsWith("http") || redirectTo.startsWith("//"),
      replace: true,
      redirectCode: pageStore.pageResponse.status
    }
  );
}
if (pageStore.pageResponse.redirect === null && pageStore.pageResponse.page === null) {
  throw createError({
    fatal: true,
    statusCode: 404
  });
}
function logError(error) {
  return error;
}
onMounted(async () => {
  if (pageStore.currentPage?.has_conditional_elements) {
    const data = await getVoixUserXP();
    await fetchCurrentPage(
      reactive({
        path: `${path}?user_xp=${JSON.stringify(data)}`
      })
    );
  }
});
</script>

<template>
  <div>
    <NuxtErrorBoundary @error="logError">
      <slot v-if="pageStore.currentPage" />
      <div
        v-else-if="!pageStore.inStudioIframe"
        class="flex justify-center"
      >
        <div class="flex flex-col space-y-4 bg-gray-100 text-gray-600 py-8 px-24 rounded-lg ">
          <span class="text-3xl font-thin">No page found.</span>
          <button
            class="bg-gray-600 text-white p-2 rounded"
            @click="loadPage"
          >
            Load page again
          </button>
        </div>
      </div>
    </NuxtErrorBoundary>
  </div>
</template>
